import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetAccountsQuery } from '@/store/accountsApi';

/**
 * Created dates for accounts in the current viewing scope, keyed by account id.
 * The historical comparison API reports a missing snapshot as `$0`, so callers
 * use this date to tell that stand-in from a real zero balance.
 */
interface AccountCreatedDatesResult {
  createdDates: ReadonlyMap<number, string>;
  isLoading: boolean;
  /** True when the accounts request failed and creation dates are unavailable. */
  isError: boolean;
}

export function useAccountCreatedDates(
  asOfDate?: string,
): AccountCreatedDatesResult {
  const authIsLoaded = useAuthIsLoaded();
  const enabled = authIsLoaded && Boolean(asOfDate);

  const { currentData, isLoading, isFetching, isUninitialized, isError } =
    useGetAccountsQuery(
      enabled
        ? {
            showClosed: false,
            asOfDate,
          }
        : skipToken,
    );

  const createdDates = useMemo(() => {
    const dates = new Map<number, string>();
    const records = currentData?.Records;
    if (!Array.isArray(records)) return dates;

    for (const account of records) {
      if (account.CreatedDate) {
        dates.set(account.AccountId, account.CreatedDate);
      }
    }
    return dates;
  }, [currentData]);

  return {
    createdDates,
    // `data` can still be the previous date while this one is in flight.
    // `currentData` is only the result for the date being requested.
    isLoading:
      enabled && !currentData && (isUninitialized || isLoading || isFetching),
    isError: enabled && isError,
  };
}
